/**
 * @file design.ts
 * @description Библиотека дизайнов сайта (phase19.16 §7.9; db-design-designs
 * §1–§3): дизайн — неизменяемый именованный набор «оформление целиком +
 * рисунок подвала (+ логотипы по выбору)». Применение ко всему сайту идёт
 * через черновик вида сайта и обычную публикацию.
 *
 * Здесь общее для сервера и кабинета:
 *   - схема визуальной части бренда в дизайне (`siteDesignBrandSchema`);
 *   - чтение сохранённого дизайна (подъём версии оформления — readSiteTheme);
 *   - `siteDesignMatches` — совпадает ли дизайн с видом сайта (значки
 *     «Текущий» и «В черновике», запрет удаления текущего) без указателей;
 *   - `applySiteDesign` — вид сайта с применённым дизайном (кнопка
 *     «Применить» и предпросмотр дизайна одним кодом);
 *   - краткая сводка для карточки списка.
 * Чтение и сводка принимают реестр шрифтов магазина (SB99, §7.12): без него
 * свои шрифты `shop:` при чтении заменяются системным.
 *
 * Свои строки «Ссылок» подвала (`theme.footerDocs`, SB142) — содержание
 * сайта, а не оформление (решение оркестратора 2026-10-19, вопрос 8 проекта
 * db-design-legal-docs): дизайн их не несёт (`siteDesignFromSnapshot` —
 * пусто), не перекрывает (`applySiteDesign` оставляет строки сайта) и не
 * сравнивает (`siteDesignMatches`). Иначе страница с дизайном, сохранённым
 * до партии документов, потеряла бы ссылку «Сертификаты».
 */

import { z } from "zod";

import { PLATFORM_FONT_REGISTRY, type SiteFontRegistry } from "./fonts.js";
import { DEFAULT_SITE_FOOTER_DOCS, type SiteFooterDocs } from "./footer-docs.js";
import { readSiteTheme, type SiteTheme } from "./schema.js";
import { canonicalJson, type SiteViewSnapshot } from "./snapshot.js";

const mediaId = z.string().min(1).max(64).nullable();

/**
 * Визуальная часть бренда в дизайне. null — «как у сайта»: поле при
 * применении не трогается (логотипы сохраняются только с галочкой «вместе с
 * логотипами», db-design-designs §2 п.1).
 */
export const siteDesignBrandSchema = z.strictObject({
  logoLightMediaId: mediaId,
  logoDarkMediaId: mediaId,
  footerDecorMediaId: mediaId,
});

export type SiteDesignBrand = z.infer<typeof siteDesignBrandSchema>;

/** Поля бренда, которые может нести дизайн (порядок — для отчётов). */
export const SITE_DESIGN_BRAND_FIELDS = ["logoLightMediaId", "logoDarkMediaId", "footerDecorMediaId"] as const;

/** Прочитанный дизайн: оформление текущей версии + визуальная часть бренда. */
export interface SiteDesignContent {
  theme: SiteTheme;
  brandVisual: SiteDesignBrand;
}

const EMPTY_BRAND: SiteDesignBrand = { logoLightMediaId: null, logoDarkMediaId: null, footerDecorMediaId: null };

/**
 * Чтение сохранённого дизайна (`SiteDesign.schema/theme/brandVisual`):
 * подъём версии оформления и умолчания для отсутствующих ключей; битая
 * визуальная часть бренда — «как у сайта». Данные новее кода — ошибка
 * (как у ревизий, upgradeTheme).
 */
export function readSiteDesign(
  raw: { schema: number; theme: unknown; brandVisual: unknown },
  registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): SiteDesignContent {
  const parsed = siteDesignBrandSchema.safeParse(raw.brandVisual);
  return {
    theme: readSiteTheme(raw.theme, raw.schema, undefined, registry).theme,
    brandVisual: parsed.success ? parsed.data : { ...EMPTY_BRAND },
  };
}

/** Копия JSON-значения (снимки и умолчания бывают заморожены). */
const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** Своих строк подвала нет — свежая копия умолчания. */
const noFooterDocs = (): SiteFooterDocs => cloneJson(DEFAULT_SITE_FOOTER_DOCS);

/**
 * Оформление без своих строк подвала — для сравнения дизайна с видом сайта.
 * Снимок в обход чтения без раздела сравнивается так же (раздел — пусто).
 */
function themeWithoutFooterDocs(theme: SiteTheme): SiteTheme {
  return { ...theme, footerDocs: noFooterDocs() };
}

/**
 * Дизайн из вида сайта: оформление целиком (без своих строк подвала — они
 * содержание сайта), рисунок подвала, логотипы — по выбору.
 */
export function siteDesignFromSnapshot(snapshot: SiteViewSnapshot, withLogos: boolean): SiteDesignContent {
  return {
    theme: themeWithoutFooterDocs(cloneJson(snapshot.theme)),
    brandVisual: {
      logoLightMediaId: withLogos ? snapshot.brand.logoLightMediaId : null,
      logoDarkMediaId: withLogos ? snapshot.brand.logoDarkMediaId : null,
      footerDecorMediaId: snapshot.brand.footerDecorMediaId,
    },
  };
}

/**
 * Совпадает ли дизайн с видом сайта: оформление одинаково (канонический
 * JSON после подъёма версии) и каждое НЕ-null поле бренда дизайна равно
 * полю вида (db-design-designs §2 п.7).
 */
export function siteDesignMatches(design: SiteDesignContent, snapshot: SiteViewSnapshot): boolean {
  if (canonicalJson(themeWithoutFooterDocs(design.theme)) !== canonicalJson(themeWithoutFooterDocs(snapshot.theme))) {
    return false;
  }
  return SITE_DESIGN_BRAND_FIELDS.every((field) => {
    const value = design.brandVisual[field];
    return value === null || snapshot.brand[field] === value;
  });
}

/**
 * Вид сайта с применённым дизайном: оформление заменяется, кроме своих строк
 * подвала (остаются строки сайта), не-null поля бренда — тоже.
 */
export function applySiteDesign(snapshot: SiteViewSnapshot, design: SiteDesignContent): SiteViewSnapshot {
  const next = cloneJson(snapshot);
  // Снимок в обход чтения может прийти без раздела — тогда своих строк нет.
  const own = (snapshot.theme.footerDocs as SiteFooterDocs | undefined) ?? noFooterDocs();
  next.theme = { ...cloneJson(design.theme), footerDocs: cloneJson(own) };
  for (const field of SITE_DESIGN_BRAND_FIELDS) {
    const value = design.brandVisual[field];
    if (value !== null) next.brand[field] = value;
  }
  return next;
}

/** Все медиа дизайна (`SiteDesign.mediaIds`): рисунки задника и поля бренда. */
export function siteDesignMediaIds(design: SiteDesignContent): string[] {
  const ids = new Set<string>(design.theme.backdrop.images.map((img) => img.mediaId));
  if (design.theme.backdrop.image) ids.add(design.theme.backdrop.image.mediaId);
  for (const field of SITE_DESIGN_BRAND_FIELDS) {
    const value = design.brandVisual[field];
    if (value !== null) ids.add(value);
  }
  return [...ids].sort();
}

/** Несёт ли дизайн поля бренда (тогда применение меняет и часть BRAND). */
export function siteDesignTouchesBrand(design: SiteDesignContent): boolean {
  return SITE_DESIGN_BRAND_FIELDS.some((field) => design.brandVisual[field] !== null);
}

/** Сводка для карточки списка: образцы цветов и шрифты. */
export interface SiteDesignSummary {
  colors: { pageBg: string; button: string; accent: string; darkBg: string; text: string };
  fonts: { heading: string; body: string; accent: string | null };
  withLogos: boolean;
}

export function siteDesignSummary(
  design: SiteDesignContent,
  registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): SiteDesignSummary {
  const { colors, fonts } = design.theme;
  // Свой шрифт показывается именем, которое дал магазин (SB99); без реестра — ключом.
  const family = (key: string): string => registry.find(key)?.family ?? key;
  return {
    colors: {
      pageBg: colors.pageBg,
      button: colors.button,
      accent: colors.accent,
      darkBg: colors.darkBg,
      text: colors.text,
    },
    fonts: {
      heading: family(fonts.heading),
      body: family(fonts.body),
      accent: fonts.accent === null ? null : family(fonts.accent),
    },
    withLogos: design.brandVisual.logoLightMediaId !== null || design.brandVisual.logoDarkMediaId !== null,
  };
}
